import { createReducer } from '@reduxjs/toolkit';
import get from 'lodash/get';
import { toast } from 'react-toastify';

import { isMfaChallengeResponse } from 'utils/mfa';
import {
  ActionFailureType,
  ActionSuccessType,
  CREATE_SESSION,
  CREATE_TIME_SLOT,
  DELETE_CALENDAR_ACCOUNT,
  GET_EXPERT_PROFILE,
  REMOVE_TIME_SLOT,
  UPDATE_GIFT_CARD_SETTING,
  UPDATE_CALENDAR_CONFIGURATIONS,
  DELETE_SESSION,
  UPDATE_SESSION,
  UPDATE_SESSION_SETTINGS,
  UPDATE_VACATION_MODE,
  USER_SPECIFIC_SITE,
  CREATE_SESSION_PACK,
  UPDATE_SESSION_PACK,
  DELETE_SESSION_PACK,
  UPDATE_PAYOUT_METHOD,
  CONFIRM_USER,
  USER_RESET_PASSWORD,
  DISCONNECT_ZOOM_ACCOUNT,
  UPDATE_ZOOM_CONFIGURATIONS,
  ENABLE_ZOOM_CO_HOST,
  UPDATE_CURRENCY_LIMITATIONS,
  UPDATE_EXPERT_VOLUNTEERING,
  PAUSE_EXPERT_VOLUNTEERING,
  RESUME_EXPERT_VOLUNTEERING,
  ARCHIVE_EXPERT_VOLUNTEERING,
  CREATE_EXPERT_VOLUNTEERING,
  SIGN_IN_WITH_TOKEN,
  SIGN_IN,
  UPDATE_EXPERT_AVAILABILITY,
  SUBMIT_ONBOARDING,
  COMPLETE_REGISTRATION,
  UPDATE_EXPERT_VISIBILITY,
  UPDATE_IN_PERSON_MEETING_LOCATIONS,
  CREATE_GIFT_CARD_APPEARANCE,
  UPDATE_GIFT_CARD_APPEARANCE,
  DELETE_GIFT_CARD_APPEARANCE,
  DELETE_USER_PICTURE,
  CREATE_USER_PICTURE,
  UPDATE_USER_PICTURE,
  UPDATE_USER_TERMS_SETTING,
  UPDATE_USER_PREFERENCES,
  VERIFY_USER_PHONE,
  SET_DEFAULT_PHONE_FOR_SITE,
  CURRENT_USER,
  UPDATE_EXPERT_DETAILS,
} from 'constants/redux-actions';
import { getErrorMessage } from 'utils/message';
import { SIGN_IN_TOAST_ID } from 'constants/common';

const initState = {};

const getError = (_, action) => {
  const error = get(action, 'error.response', {});

  // A 403 step-up is shown by the site banner (or the sign-in step), not as a toast
  if (isMfaChallengeResponse(error.data)) return initState;

  const message = getErrorMessage(error);

  toast.error(message, { toastId: message.slice(0, 10) });

  return initState;
};

const setSuccess = (message, options) => () => {
  if (options) toast.success(message, options);
  else toast.success(message);

  return initState;
};

const signedInUnlessChallenged = (state, action) =>
  isMfaChallengeResponse(get(action, 'payload.data'))
    ? initState
    : setSuccess('Sign in successfully.', { toastId: SIGN_IN_TOAST_ID })();

// It's used to observe actions and show toast messages for different actions
const toastReducer = createReducer(initState, {
  [ActionFailureType(GET_EXPERT_PROFILE)]: (_, action) => {
    const status = get(action, 'error.response.status');

    if (status === 401) {
      const message = 'Please sign in before view this expert profile';
      toast.error(message);
    }

    return initState;
  },
  [ActionFailureType(USER_SPECIFIC_SITE)]: getError,
  [ActionSuccessType(UPDATE_VACATION_MODE)]: setSuccess('Update vaction mode successfully'),
  [ActionFailureType(REMOVE_TIME_SLOT)]: getError,
  [ActionSuccessType(CREATE_TIME_SLOT)]: setSuccess('Scheduled slot successfully!'),
  [ActionSuccessType(UPDATE_CALENDAR_CONFIGURATIONS)]: setSuccess('Update calendar configurations successfully'),
  [ActionFailureType(UPDATE_CALENDAR_CONFIGURATIONS)]: getError,
  [ActionSuccessType(DELETE_CALENDAR_ACCOUNT)]: setSuccess('Disconnect calendar account successfully'),
  [ActionFailureType(DELETE_CALENDAR_ACCOUNT)]: getError,
  [ActionSuccessType(UPDATE_GIFT_CARD_SETTING)]: setSuccess('Update accepting gift card successfully'),
  [ActionFailureType(UPDATE_GIFT_CARD_SETTING)]: getError,
  [ActionSuccessType(CREATE_SESSION)]: setSuccess('Created session successfully!'),
  [ActionSuccessType(UPDATE_SESSION)]: setSuccess('Updated session successfully!'),
  [ActionSuccessType(DELETE_SESSION)]: setSuccess('Deleted session successfully!'),
  [ActionFailureType(DELETE_SESSION)]: getError,
  [ActionSuccessType(UPDATE_SESSION_SETTINGS)]: setSuccess('Updated successfully!'),
  [ActionFailureType(UPDATE_SESSION_SETTINGS)]: getError,
  [ActionSuccessType(CREATE_SESSION_PACK)]: setSuccess('Created pack successfully!'),
  [ActionSuccessType(UPDATE_SESSION_PACK)]: setSuccess('Updated pack successfully!'),
  [ActionSuccessType(DELETE_SESSION_PACK)]: setSuccess('Deleted pack successfully!'),
  [ActionFailureType(DELETE_SESSION_PACK)]: getError,
  [ActionSuccessType(UPDATE_PAYOUT_METHOD)]: setSuccess('Update payout method successfully!'),
  [ActionFailureType(UPDATE_PAYOUT_METHOD)]: getError,
  [ActionSuccessType(CONFIRM_USER)]: setSuccess('Your email has been confirmed. Thank you!'),
  [ActionFailureType(CONFIRM_USER)]: getError,
  [ActionSuccessType(USER_RESET_PASSWORD)]: setSuccess('Reset your password successfully!'),
  [ActionFailureType(USER_RESET_PASSWORD)]: getError,
  [ActionSuccessType(REMOVE_TIME_SLOT)]: setSuccess('Remove time slot successfully'),
  [ActionSuccessType(DISCONNECT_ZOOM_ACCOUNT)]: setSuccess('Disconnect Zoom account successfully!'),
  [ActionFailureType(DISCONNECT_ZOOM_ACCOUNT)]: getError,
  [ActionSuccessType(UPDATE_ZOOM_CONFIGURATIONS)]: setSuccess('Update Zoom configurations successfully!'),
  [ActionFailureType(UPDATE_ZOOM_CONFIGURATIONS)]: getError,
  [ActionSuccessType(ENABLE_ZOOM_CO_HOST)]: setSuccess('Enabled Co-host for zoom account is successfully!'),
  [ActionFailureType(ENABLE_ZOOM_CO_HOST)]: getError,
  [ActionSuccessType(UPDATE_CURRENCY_LIMITATIONS)]: setSuccess('Updated currency limitations successfully!'),
  [ActionSuccessType(UPDATE_EXPERT_VOLUNTEERING)]: setSuccess('Updated expert volunteering successfully!'),
  [ActionFailureType(UPDATE_EXPERT_VOLUNTEERING)]: getError,
  [ActionSuccessType(PAUSE_EXPERT_VOLUNTEERING)]: setSuccess('Paused expert volunteering successfully!'),
  [ActionFailureType(PAUSE_EXPERT_VOLUNTEERING)]: getError,
  [ActionSuccessType(RESUME_EXPERT_VOLUNTEERING)]: setSuccess('Resumed expert volunteering successfully!'),
  [ActionFailureType(RESUME_EXPERT_VOLUNTEERING)]: getError,
  [ActionSuccessType(ARCHIVE_EXPERT_VOLUNTEERING)]: setSuccess('Archived expert volunteering successfully!'),
  [ActionFailureType(ARCHIVE_EXPERT_VOLUNTEERING)]: getError,
  [ActionSuccessType(CREATE_EXPERT_VOLUNTEERING)]: setSuccess('Created new expert volunteering successfully!'),
  [ActionFailureType(CREATE_EXPERT_VOLUNTEERING)]: () => toast.error('Create new expert volunteering failed!'),
  [ActionFailureType(SIGN_IN_WITH_TOKEN)]: getError,
  [ActionSuccessType(SIGN_IN)]: signedInUnlessChallenged,
  [ActionSuccessType(UPDATE_EXPERT_AVAILABILITY)]: setSuccess('Updated availability successfully!'),
  [ActionFailureType(UPDATE_EXPERT_AVAILABILITY)]: getError,
  [ActionSuccessType(SUBMIT_ONBOARDING)]: setSuccess(
    'Profile submitted. Approval can take up to 24 hours, but usually happens much faster than that.',
  ),
  [ActionFailureType(SUBMIT_ONBOARDING)]: getError,
  [ActionFailureType(COMPLETE_REGISTRATION)]: getError,
  [ActionSuccessType(UPDATE_EXPERT_VISIBILITY)]: setSuccess('Updated expert profile visibility successfully!'),
  [ActionFailureType(UPDATE_EXPERT_VISIBILITY)]: getError,
  [ActionSuccessType(UPDATE_IN_PERSON_MEETING_LOCATIONS)]: setSuccess(
    'Updated in-person meeting locations successfully!',
  ),
  [ActionFailureType(UPDATE_IN_PERSON_MEETING_LOCATIONS)]: getError,
  [ActionSuccessType(CREATE_GIFT_CARD_APPEARANCE)]: setSuccess('Created gift card appearance successfully!'),
  [ActionFailureType(CREATE_GIFT_CARD_APPEARANCE)]: getError,
  [ActionSuccessType(UPDATE_GIFT_CARD_APPEARANCE)]: setSuccess('Updated gift card appearance successfully!'),
  [ActionFailureType(UPDATE_GIFT_CARD_APPEARANCE)]: getError,
  [ActionSuccessType(DELETE_GIFT_CARD_APPEARANCE)]: setSuccess('Deleted gift card appearance successfully!'),
  [ActionFailureType(DELETE_GIFT_CARD_APPEARANCE)]: getError,
  [ActionSuccessType(CREATE_USER_PICTURE)]: setSuccess('Created successfully!'),
  [ActionFailureType(CREATE_USER_PICTURE)]: getError,
  [ActionSuccessType(UPDATE_USER_PICTURE)]: setSuccess('Updated successfully!'),
  [ActionFailureType(UPDATE_USER_PICTURE)]: getError,
  [ActionSuccessType(DELETE_USER_PICTURE)]: setSuccess('Deleted successfully!'),
  [ActionFailureType(DELETE_USER_PICTURE)]: getError,
  [ActionSuccessType(UPDATE_USER_TERMS_SETTING)]: setSuccess('Updated successfully!'),
  [ActionFailureType(UPDATE_USER_TERMS_SETTING)]: getError,
  [ActionSuccessType(UPDATE_USER_PREFERENCES)]: setSuccess('Updated successfully!'),
  [ActionFailureType(UPDATE_USER_PREFERENCES)]: getError,
  [ActionSuccessType(VERIFY_USER_PHONE)]: setSuccess('Verified successfully!'),
  [ActionSuccessType(SET_DEFAULT_PHONE_FOR_SITE)]: setSuccess('Set default phone number for this tenant successfully!'),
  [ActionFailureType(SET_DEFAULT_PHONE_FOR_SITE)]: getError,
  [ActionFailureType(CURRENT_USER)]: getError,
  [ActionSuccessType(UPDATE_EXPERT_DETAILS)]: setSuccess('Updated successfully!'),
  [ActionFailureType(UPDATE_EXPERT_DETAILS)]: getError,
  [ActionSuccessType(SIGN_IN_WITH_TOKEN)]: signedInUnlessChallenged,
});

export default toastReducer;
